<template>
  <div>
    <NuxtLayout name="default">
      <!-- Hero Component -->
      <Hero
        v-if="$page?.studentsReviews.title"
        :title="$page?.studentsReviews.title"
        :title-label-text="$page?.studentsReviews.titleLabelText"
        :description="$page.studentsReviews.titleDescription"
        :link="$page.studentsReviews.titleLink"
        @btn-click="handleScrollToRfi"
      ></Hero>
      <!--SectionTestimonialColumns Component -->
      <SectionTestimonialColumns
        v-if="$page?.studentsReviews.testimonialTitle"
        :title="$page?.studentsReviews.testimonialTitle"
        :items="$page.studentsReviews.testimonials"
        :display-label="false"
        @slideFocus="handleHover"
        @nextSlideClick="handleNextSlide"
        @previousSlideClick="handlePreviousSlide"
      >
        <template #body>
          <div
            class="row col-md-8"
            v-html="$page.studentsReviews.testimonialDescription"
          ></div>
        </template>
      </SectionTestimonialColumns>
      <!-- World Component -->
      <SectionConnectionsWorld
        v-if="$page?.studentsReviews.connectionsTitle"
        :label-text="$page?.studentsReviews.connectionsTitle"
        :quote-text="$page.studentsReviews.connectionsQuote"
        :name="$page.studentsReviews.connectionsStudentName"
        :designation="$page.studentsReviews.connectionsStudentClass"
        :image-source="$page.studentsReviews.connectionsBgImage"
        :image-alt="$page.studentsReviews.connectionsBgImageAlt"
        :description="$page.studentsReviews.connectionsDescription"
      ></SectionConnectionsWorld>
      <!-- SectionVideoReview Component -->
      <SectionVideoApollo
        :youtube-id="$page?.studentsReviews.video.youtubeId!"
        :title="$page?.studentsReviews.video.description!"
        titleSize="xl"
        :bg-image-source="$page?.studentsReviews.video.imageSource!"
        :bg-shadow-opacity="0.75"
        :applyMdBlockClass="false"
        modal-text-variant="white"
        label=""
        @modal-open="
          handleModalEvent(
            'open',
            'Sun Devils in their own words',
            'play button'
          )
        "
        @modal-close="
          handleModalEvent(
            'close',
            'Sun Devils in their own words',
            'close cross'
          )
        "
      ></SectionVideoApollo>
      <!--SectionReviewsQuote Component -->
      <SectionReviewsQuote
        v-if="$page?.studentsReviews.studentQuote"
        :quote-text="$page?.studentsReviews.studentQuote.quote"
        :img-src="$page?.studentsReviews.studentQuote.authorImage!"
        :img-alt="$page?.studentsReviews.studentQuote.authorImageAlt!"
        :name="$page?.studentsReviews.studentQuote.authorName!"
        :designation="$page?.studentsReviews.studentQuote.authorDesignation!"
      />
      <!--OnlineDegrees Component -->
      <section
        class="pb-space-md my-space-lg my-lg-space-xxl"
        v-if="$page?.studentsReviews.onlineTitle"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <OnlineDegrees
                :degrees-title="$page.studentsReviews.onlineTitle"
                :degrees-description="$page.studentsReviews.onlineTitle2"
                :bg-image-source="$page?.studentsReviews.onlineBgImage"
                :bg-image-alt="$page.studentsReviews.onlineBgImageAlt"
                :title="$page.studentsReviews.onlineQuote"
                :label="$page.studentsReviews.onlineStudentName"
                :year="$page.studentsReviews.onlineStudentClass"
                :footer-text="$page.studentsReviews.onlineFooter"
                :marketing-image-src="$page.studentsReviews.onlineDegreeImage!"
                :marketing-image-alt="$page.studentsReviews.onlineDegreeImageAlt!"
              ></OnlineDegrees>
            </div>
          </div>
        </div>
      </section>
      <!-- RFI Component -->
      <SectionRfiMultistepNew
        :bg-image="RfiBgImage"
        ref="rfiElRef"
        class="mb-space-lg mb-lg-space-xxl"
      ></SectionRfiMultistepNew>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import RfiBgImage from "@/assets/img/new/home-page/RFI-Image-v2.jpg";
import SectionReviewsQuote from "@/components/students/SectionReviewsQuote.vue";
import { StudentsReviews } from "@/server/resolvers/resolvers-types";
import { scrollToTarget } from "@/utils";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { SectionTestimonialColumns } from "@rds-vue-ui/section-testimonial-columns";
import { useWindowSize } from "@vueuse/core";
import SectionRfiMultistepNew from "~/components/home-page/SectionRfiMultistepNew.vue";
import Hero from "~/components/students/Hero.vue";
import OnlineDegrees from "~/components/students/OnlineDegrees.vue";
import SectionConnectionsWorld from "~/components/students/SectionConnectionsWorld.vue";

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

interface StudentsReviewsResponse {
  studentsReviews: StudentsReviews;
}

const UtilityService = useUtilityService();
const config = useRuntimeConfig();
const { width } = useWindowSize();

const $page = await usePageQuery<StudentsReviewsResponse>(
  `#graphql
    query studentsReviews($studentsReviewsId: ID!) {
      studentsReviews(id: $studentsReviewsId) {
        metaTitle
        metaDescription
        detailPage
        title
        titleLabelText
        titleDescription
        titleLink {
          text
          uri
        }
        testimonialTitle
        testimonialDescription
        testimonials {
          name
          quote
          designation
          designationLineTwo
          image
          imageAlt
        }
        connectionsTitle
        connectionsQuote
        connectionsStudentName
        connectionsStudentClass
        connectionsBgImage
        connectionsBgImageAlt
        connectionsDescription
        video {
          description
          youtubeId
          imageSource
          imageAlt
          uploadDate
          duration
        }
        studentQuote {
          quote
          authorName
          authorDesignation
          authorImage
          authorImageAlt
        }
        onlineBgImage
        onlineBgImageAlt
        onlineTitle
        onlineTitle2
        onlineQuote
        onlineStudentName
        onlineStudentClass
        onlineFooter
        onlineDegreeImage
        onlineDegreeImageAlt
      }
    }
  `,
  {
    studentsReviewsId: "c22d3751-896f-479d-b465-7a1bb0627b9e",
  },
  {
    key: "students-reviews",
  }
);

useHead({
  title: $page?.studentsReviews.metaTitle,
  meta: [
    { name: "Title", content: $page?.studentsReviews.metaTitle },
    {
      name: "Description",
      content: $page?.studentsReviews.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.studentsReviews.detailPage}`
      ),
    },
  ],
  script: [
    useSchemaService().generateStudentsReviewsSchema($page?.studentsReviews),
  ],
});

const handleScrollToRfi = (e: Event) => {
  e.preventDefault();
  let offsetVal: number = 59;
  if (width.value < 992) {
    offsetVal = 65;
  }
  scrollToTarget("asuo-rfi-section", { offset: offsetVal });
};

const handleModalEvent = (action: string, section: string, text: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text
  );
};

const handleHover = (event: Event, index: number) => {
  const studentName = $page?.studentsReviews.testimonials[index].name;
  analyticsComposable.trackCollapseEvent(
    "onhover",
    "open",
    "hover",
    "main content",
    $page!.studentsReviews.testimonialTitle.toLowerCase(),
    studentName ? studentName.toLowerCase() : ""
  );
};

const handleNextSlide = () => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "carousel",
    "main content",
    $page!.studentsReviews.testimonialTitle.toLowerCase(),
    "right chevron"
  );
};

const handlePreviousSlide = () => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "carousel",
    "main content",
    $page!.studentsReviews.testimonialTitle.toLowerCase(),
    "left chevron"
  );
};
</script>

<style scoped lang="scss">
:deep(.titleStandard) {
  padding-bottom: 0px !important;
}
</style>
